import { DirectoryOptions, HostedConfig } from './utils/constants'
import { CronScheduleTypes, DATA_SOURCE_TYPES, UserStatuses } from '../../constants'
import { AttributeDataSource } from '../attributes/attributesSlice'
import { DataSource } from '../../services/api/apiTypes'
import { ClassificationObject } from '../classifications/classificationsSlice'

export type RouteParams = {
  subPage: string
  subSubPage: string
  subPageId?: string
  activeTab?: string
  tabId: string
  action?: string
}

export enum AccessControlUserType {
  Internal = 'INTERNAL',
  External = 'EXTERNAL'
}

export enum AccessControlEmployeeType {
  Contractor = 'CONTRACTOR',
  Employee = 'EMPLOYEE',
  Unknown = 'UNKNOWN'
}

export enum DataSensitivityLevels {
  High = 'HIGH',
  Medium = 'MEDIUM',
  Low = 'LOW',
  None = 'NONE',
  All = 'ALL'
}

export enum accessControlMemberTypes {
  Direct = 'DIRECT',
  Inherited = 'INHERITED'
}

export interface AccessControlUserListItem {
  id?: string
  name: string
  userType?: AccessControlUserType
  departmentName?: string
  region?: string
  objectsCount?: number
  datasourcesCount?: number
  entitiesCount?: number
  dataPrivilegeLevel?: DataSensitivityLevels
  email?: string
  entityId?: string
  accountsCount?: number
  permissionsCount?: number
}

export interface AccessControlGroupListItem {
  id?: string
  name: string
  type?: string
  objectsCount?: number
  datasourcesCount?: number
  entitiesCount?: number
  dataPrivilegeLevel?: DataSensitivityLevels
}

export enum AccessControlUserTypes {
  guest = 'GUEST',
  external = 'EXTERNAL',
  internal = 'INTERNAL'
}

export interface IGetAccessControlUsersFilterParams {
  page?: number
  id?: number
  groupId?: number
  departmentName?: string
  dataPrivilegeLevels?: string[]
  employeeType?: string
  entityId?: string
  attributeId?: string
  datasourceId?: string
  datasourceIds?: string[]
  sensitivityLevel?: DataSensitivityLevels
  sensitivityLevels?: DataSensitivityLevels[]
  entityTypes?: string[]
  risky?: boolean
  attributeIds?: string[]
  objectIds?: string[]
  hasEntityAccess?: boolean
  accessModes?: AccessControlAccessMode[]
  isDirectMembership?: boolean
  emailId?: string
  searchText?: string
  hasMembers?: boolean
  forAttributes?: boolean
  dateFilter?: {
    key?: string
    start?: number
    end?: number
  }
  managerId?: string
  managerIds?: number[]
  accountStatus?: UserStatuses[]
  userType?: AccessControlUserTypes[]
  lastLoggedInFilter?: {
    start?: string
    end?: string
  }
  accessControlGroupIds?: number[]
  isScanned?: boolean
}
export interface IGetAccessControlGroupsFilterParams {
  page?: number
  id?: number
  groupId?: number
  dataPrivilegeLevels?: string[]
  entityId?: string
  datasourceId?: string
  entityTypes?: string[]
  hasEntityAccess?: boolean
  hasMembers?: boolean
  employeeType?: string
  isDirect?: boolean
  attributeIds?: string[]
  searchText?: string
}

export type GetAccessControlUserByObjectIdFilterParams = {
  id: string
  pageSize: number
  after?: string
  department?: string
  employeeType?: string[]
  groups?: string[]
  sensitivity?: string[]
}

export interface AccessControlUserGroupByDepartmentItem {
  departmentName?: string
  memberCount?: number
  dataPrivilegeMemberCount?: number
}

export interface AccessControlUsersSummary {
  employeesCount: number
  contractorsCount: number
  totalEmployeesCount: number
  totalContractorsCount: number
}

export interface AccessControlUserDetailsOverview extends AccessControlUserListItem {
  employeeType?: string
  groupsCount: number
  attributesCount: number
  highAttributesCount?: number
  sensitiveObjects?: number
  highSensitiveOwnedObjects?: number
  openAccessObjectsCount?: number
  excessiveAccessObjectsCount?: number
  totalObjectsCount?: number
  lastFileActivityTime?: string
}

export interface AccessControlGroupDetailsOverview extends AccessControlGroupListItem {
  usersCount: number
  groupsCount: number
  attributesCount: number
  objectsCount?: number
  subGroups?: AccessControlGroupListItem[]
  sensitiveObjects?: number
  email?: string
}

export interface IParamsId {
  id: string
}

export interface AccessControlUserDatasource {
  id: string
  type: DATA_SOURCE_TYPES
  name: string
  dataPrivilegeLevel: DataSensitivityLevels
  entitiesCount: number
  attributesCount: number
  riskDensity?: number
  objectsCount: number
  sensitiveObjects?: number
}

export interface AccessControlUserEntities {
  id: number
  type: string
  name: string
  riskStatus: boolean
  datasourcesCount: number
  attributesCount: number
  objectsCount: number
}

export enum AccessControlAccessTypes {
  open = 'Open Access',
  excessive = 'Excessive Access',
  external = 'External Access'
}

export type AccessControlObjectSummary = {
  objectsCount?: number
  ownersCount?: number
  dataSourcesCount?: number
}

export type AccessControlObject = {
  id: number
  mongoId?: string
  bucketName?: string
  parentAccountName?: string
  name: string
  owner?: string
  ownerInfo?: {
    name: string
    email: string
    id: string
  }
  sensitivity?: DataSensitivityLevels
  groupAccess?: Array<{ id: string; name: string }>
  groupAccessCount?: number
  userAccess?: Array<{ id: string; name: string }>
  userAccessCount?: number
  entities?: Array<{ id: string; name: string }>
  entitiesCount?: number
  attributes: Array<{ id: string; name: string; internalName: string }>
  attributesCount?: number
  labels: Array<{ id: string; name: string }>
  isDetailsReceived?: boolean
  objectLink?: string
}
export type AccessControlObjectCard = {
  ownerName?: string
  ownerId?: string
  ownerEmail?: string
  objectsCount?: number
  sensitiveObjectsCount?: number
}

export type AccessControlObjectCardBySensitivity = {
  [DataSensitivityLevels.High]?: number
  [DataSensitivityLevels.Medium]?: number
  [DataSensitivityLevels.Low]?: number
  [DataSensitivityLevels.None]?: number
  totalObjects?: number
}

export type AccessControlGlobalObjectCard = {
  dataSourceId: string
  dataSourceName: string
  dataSourceType: DATA_SOURCE_TYPES
  objectsCount: number
  sensitiveObjectsCount: number
  ownersCount: number
}
export interface AccessControlUsersWidget {
  totalUsers: number
  privilegeUsers: number
  employeeUsers: number
  contractorUsers: number
}

export interface AccessControlGroupsWidget {
  totalGroups: number
  privilegeGroups: number
}

export interface AccessControlGroupCard {
  groupId: string
  groupName: string
  objectsCount: number
  attributesCount?: number
}

export enum IdpStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Unknown = 'UNKNOWN'
}
export interface IdpProvider {
  id: string
  name: string
  type: string
  status: IdpStatus
}
export interface AccessControlIdpWidget {
  totalProviders: number
  providers: IdpProvider[]
}

export interface AccessControlEntityWidget {
  employeesCount: number
  contractorsCount: number
  employeesUsersCount: number
  contractorsUsersCount: number
  totalEntities: number
}

export interface Access360Widget {
  usersCount: number
  openAccessObjects: number
  execessiveAccessObjects: number
  externalAccessObjects: number
  objectsCount: number
  usersWithAccessToEntities: number
}

export interface Access360WidgetIndexData {
  [key: string]: Access360Widget
}

export enum ObjectsImpactedAccessTypeFilter {
  OpenAccess = 'hasOpenAccess',
  ExternalAccess = 'hasExternalAccess',
  ExcessiveAccess = 'hasExcessiveAccess'
}
export interface AccessControlObjectsWithImpactedAccess {
  totalObjects: number
  impactedObjects: number
  datasources: {
    id?: string
    name?: string
    type?: DATA_SOURCE_TYPES
    totalObjects?: number
    impactedObjects?: number
  }[]
}

export interface AccessControlObjectsWithImpactedAccessByDept {
  totalObjects: number
  totalDepartments: number
  totalOwners: number
  departments: {
    name?: string
    totalObjects?: number
  }[]
}

export const SENSITIVITY_LEVELS = [
  DataSensitivityLevels.All,
  DataSensitivityLevels.High,
  DataSensitivityLevels.Medium,
  DataSensitivityLevels.Low
]

export type AccessControlUserEntityCard = {
  id: number
  name: string
  entitiesCount: number
  atRiskCount: number
  associatedDatasources: EntityTypeGroupByDatasource[]
}

export type EntityTypeGroupByDatasource = {
  id: string
  name: string
  type: DATA_SOURCE_TYPES
}

export type AccessControlEntityAccessWidget = {
  totalUsers: number
  entityTypes: { id: string; name: string; entityCount: number; accessibleEntityCount: number }[]
}
// User Attributes
export type AccessControlUserAttributesWidget = {
  attributeSensitivity: DataSensitivityLevels
  count: number
}

export type AccessControlUserAttributeCard = {
  attributeId: string
  attributeName: string
  sensitivityLabel: string
  dataSourcesCount: number
  dataSources: AttributeDataSource[]
}

export type AccessControlAccessibleWidget = {
  usersCount: number
  groupsCount: number
  rolesCount: number
  resourcesCount: number
}

export type AccessControlFilters = {
  departments?: string[]

  entityTypes?: {
    id: string
    name: string
  }[]
  datasources?: {
    id: string
    name: string
  }[]
  attributes?: {
    id: string
    name: string
    internalName: string
  }[]
}

export enum RiskFilterTypes {
  Risky = 'At Risk',
  NotRisky = 'Not Risk'
}

export const SensitivityLevelsColors = {
  [DataSensitivityLevels.High]: '#EB6818',
  [DataSensitivityLevels.Medium]: '#F49E17',
  [DataSensitivityLevels.Low]: '#2EB67D',
  [DataSensitivityLevels.None]: '#959DA5'
}

export enum UserObjectsTabs {
  Owned = 'Owned',
  Accessible = 'Accessible',
  OpenAccess = 'OpenAccess',
  DirectAccess = 'DirectAccess',
  AccessViaGroup = 'AccessViaGroup',
  ExternalAccess = 'ExternalAccess',
  ExcessiveAccess = 'ExcessiveAccess',
  All = 'All'
}

type CronSchedule = {
  cronType: CronScheduleTypes
  humanReadableSchedule?: {
    frequencyType: string
    frequencyValue: number
  }
  cronExpression?: string
}

export type IdentityProviderParams = {
  id?: string
  name: string
  email: string
  domain?: string
  endpoint?: string
  username?: string
  password?: string
  sslEnabled?: boolean
  sslCert?: string
  delegatedCredential?: string
  accountJson?: string
  entraAd?: boolean
  datasourceIds?: string[]
  cronSchedule?: CronSchedule
  type: DirectoryOptions
  hostedConfig?: HostedConfig
  accessKey?: string
  secretKey?: string
}

export type TestConnectionParams = {
  type: DirectoryOptions
  accountJson?: string
  delegatedCredential?: string
  domain?: string
  endpoint?: string
  username?: string
  password?: string
  sslEnabled?: boolean
  sslCert?: string
  hostedConfig?: HostedConfig
  accessKey?: string
  secretKey?: string
}
export interface DirectoryService {
  id: string
  name: string
  idpType: string
  lastSyncedAt?: string
  datasourceCount: number
  ownerEmail?: string
  description?: string
  datasources?: DataSource[]
  cronSchedule?: CronSchedule
}

export type AlertsOverviewWidget = {
  id: string
  name: string
  status: string
  severity: string
  generatedAt: string
  assignees: number
  policyType: string
  datasourceId: string
}
export type StaleFilesWidget = {
  totalFiles: number
  staleFiles: number
}

export enum AccessReportFilters {
  Sensitivity = 'sensitivity',
  EmployeeType = 'employeeType',
  Department = 'departmentName',
  DataSource = 'datasourceId',
  Objects = 'objectIds',
  User = 'accessControlUserIds',
  Group = 'accessControlGroupIds',
  DatasourceIds = 'datasourceIds',
  EntityType = 'entityTypeIds',
  Attribute = 'attributeTypeIds',
  Entity = 'entityIds',
  HasEntityAccess = 'hasEntityAccess',
  IsOwner = 'isOwner',
  AccessModes = 'accessModes'
}

export enum AccessControlAccessMode {
  Read = 'READ',
  Write = 'WRITE'
}

export enum StateFilesAccessTypes {
  Read = 'READ',
  Write = 'WRITE',
  Delete = 'DELETE',
  Create = 'CREATE',
  Other = 'OTHER'
}

export type ActivityLogItem = {
  datasource?: {
    id: string
    name: string
    type: DATA_SOURCE_TYPES
  }
  startTime: string
  endTime: string
  identity?: {
    id: string
    name: string
  }
  readCount: number
  writeCount: number
  createCount: number
  deleteCount: number
  otherCount: number
}

export type RecentAccessItem = ClassificationObject & {
  eventTime: string
  datasource?: {
    id: string
    name: string
    type: DATA_SOURCE_TYPES
  }
  identity?: {
    id: string
    name: string
  }
  accessType: string
  rawObjectUrl?: string
}

export type AccessControlObjectsCount = {
  ownedTotalObjects: number
  ownedSensitiveObjects: number
  openAccessTotalObjects: number
  openAccessSensitiveObjects: number
  directAccessTotalObjects: number
  directAccessSensitiveObjects: number
  accessViaGroupTotalObjects: number
  accessViaGroupSensitiveObjects: number
  externalAccessTotalObjects: number
  excessiveAccessTotalObjects: number
}

export type AccessibleAttributesWidget = {
  id: string
  attributeName: string
  count: number
}

export interface UserDatasourcesWidget {
  id: string
  name: string
  type: string
  count: number
  sensitiveCount: number
}

export interface SalesforceMonitoredUsersWidgetData {
  internalAllUsers: number
  internalStaleUsers: number
  externalAllUsers: number
  externalStaleUsers: number
}

export interface SalesforceObjectsWidgetData {
  openAccessCount: number
  excessiveAccessCount: number
}

export interface SalesforceUnresolvedAlertsWidgetData {
  usersImpacted: number
  objectsImpacted: number
  alerts: { id: string; name: string }[]
}

export interface SalesforceUserAccessWidgetData {
  highSensitiveUsers: number
  totalUsers: number
}

export interface SalesforceUserCountsWidgetData {
  totalUsers: number
  activeUsers: number
  staleUsers: number
  externalUsers: number
  suspendedUsers: number
  privilegedUsers: number
  deletedUsers: number
}

export interface SalesforceUserDetailsWidgetData {
  monitoredObjects: number
  monitoredAccounts: number
}

export interface SalesforceDashboardParams {
  datasourceId: string
}
